import { ModalTitle } from '@strava/ui/Modal';
import React, { useEffect, useRef, useState } from 'react';
import clsx from '@strava/ui/clsx';
import { isEmpty, isNil } from 'lodash-es';
import Tooltip from '@strava/ui/Tooltip';
import { Markup } from 'interweave';
import NavigationHelpNormalXsmall from '@strava/icons/NavigationHelpNormalXsmall';
import PropTypes from 'prop-types';
import { analyticsPageName, t } from '../../../../shared/onboardingUtils';
import { useOnboardingContext } from '../../../../state/OnboardingContext';
import styles from './CreateProfile.scss';
import {
  CLEAR_ALERT,
  UPDATE_CURRENT_ATHLETE
} from '../../../../state/Onboarding.actions';
import {
  INPUT_IDS,
  REGISTRATION_FLOW
} from '../../../../shared/onboardingConstants';
import DropdownDatePicker from '../../../../../../components/shared/DropdownDatePicker';
import StravaDropdownSelect from '../../../../../../components/shared/StravaDropdownSelect/StravaDropdownSelect';
import { GENDER } from '../../../../../../constants/appConstants';

import useNavigateToNextStep from '../../../../shared/useNavigateToNextStep';
import ActionButton from '../../../ActionButton/ActionButton';
import ModalBodyWithAlerts from '../../../ModalBodyWithAlerts';
import OnboardingModalActions from '../../../OnboardingModalActions';

const PREFIX = 'create_your_profile';

const CreateProfile = () => {
  const { state, dispatch, track } = useOnboardingContext();
  const { stepForward, isProcessing } = useNavigateToNextStep({
    state,
    dispatch,
    track
  });

  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);

  const { currentAthlete, alert, locale } = state;
  const { firstName, lastName, dob, gender } = currentAthlete;

  const [isAgeGated] = useState(!isEmpty(dob));

  const { year, month, day } = dob || {};

  const dobObject = (() => {
    if (!isNil(year) && !isNil(month) && !isNil(day)) {
      return new Date(year, month - 1, day); // jsDate starts with 0 for Jan
    }
    return null;
  })();

  const isInvalidLastName = alert?.target === INPUT_IDS.lastName;

  const isInvalidFirstName = alert?.target === INPUT_IDS.firstName;

  const isInvalidBirthday =
    alert?.target === (INPUT_IDS.month || INPUT_IDS.day || INPUT_IDS.year);

  const isInvalidGender = alert?.target === INPUT_IDS.gender;

  useEffect(() => {
    track({
      page: analyticsPageName(REGISTRATION_FLOW.createProfile),
      action: 'screen_enter'
    });
  }, [track]);

  useEffect(() => {
    if (isInvalidFirstName) {
      firstNameInputRef.current.focus();
    } else if (isInvalidLastName) {
      lastNameInputRef.current.focus();
    }
  }, [isInvalidFirstName, isInvalidLastName, alert]);

  const handleNameChange = (event) => {
    dispatch({ type: CLEAR_ALERT });
    const { target } = event;
    const { name } = target;

    dispatch({
      type: UPDATE_CURRENT_ATHLETE,
      payload: { [name]: target.value }
    });

    const nameChangeEvent = new CustomEvent('OnBoardingNameChange', {
      bubbles: true
    });
    target.dispatchEvent(nameChangeEvent);
  };

  const handleDobChange = (date) => {
    dispatch({ type: CLEAR_ALERT });
    dispatch({
      type: UPDATE_CURRENT_ATHLETE,
      payload: { dob: date }
    });
  };

  const handleGenderChange = ({ value }) => {
    dispatch({ type: CLEAR_ALERT });
    dispatch({
      type: UPDATE_CURRENT_ATHLETE,
      payload: { gender: value }
    });
  };

  const dobErroredControls = (() => {
    return isInvalidBirthday ? alert.controls : [];
  })();

  const genderOptions = (() => {
    const options = [];
    Object.values(GENDER).forEach((value) => {
      options.push({
        label: t(`${PREFIX}.profile_details.gender_select.${value}`),
        value
      });
    });
    return options;
  })();

  const genderControlCustomStyles = (() => {
    return {
      valueContainer: (provided) => ({
        ...provided,
        padding: '12px 16px'
      })
    };
  })();

  return (
    <>
      <ModalTitle id="onboarding-step-title">{t(`${PREFIX}.title`)}</ModalTitle>
      <ModalBodyWithAlerts alert={alert}>
        <p className={styles.subtitle}>{t(`${PREFIX}.subtitle`)}</p>

        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label
              htmlFor={INPUT_IDS.firstName}
              className={clsx(isInvalidFirstName && styles.error)}
            >
              {t(`${PREFIX}.profile_details.first_name`)}
            </label>
            <input
              id={INPUT_IDS.firstName}
              name={INPUT_IDS.firstName}
              type="text"
              className={clsx(
                styles.formControl,
                isInvalidFirstName && styles.error
              )}
              value={firstName}
              onChange={handleNameChange}
              autoComplete="off"
              ref={firstNameInputRef}
            />
          </div>

          <div className={styles.formGroup}>
            <label
              htmlFor={INPUT_IDS.lastName}
              className={clsx(isInvalidLastName && styles.error)}
            >
              {t(`${PREFIX}.profile_details.last_name`)}
            </label>
            <input
              id={INPUT_IDS.lastName}
              name={INPUT_IDS.lastName}
              type="text"
              className={clsx(
                styles.formControl,
                isInvalidLastName && styles.error
              )}
              onChange={handleNameChange}
              value={lastName}
              autoComplete="off"
              ref={lastNameInputRef}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label
              htmlFor="birthday"
              className={clsx(isInvalidBirthday && styles.error)}
            >
              <span>{t(`${PREFIX}.profile_details.birthday`)}</span>
              <CustomTooltip content={t(`${PREFIX}.profile_details.why_dob`)} />
            </label>
            <DropdownDatePicker
              key={`${INPUT_IDS.birthday}}-${alert?.id}`}
              name={INPUT_IDS.birthday}
              className={styles.datepicker}
              dateChangeCallback={handleDobChange}
              erroredControls={dobErroredControls}
              locale={locale}
              required={true}
              dateSelected={dobObject}
              disabled={isAgeGated}
              focusTarget={alert?.target}
            />
          </div>

          <div className={styles.formGroup}>
            <label
              htmlFor={INPUT_IDS.gender}
              className={clsx(isInvalidGender && styles.error)}
            >
              <span>{t(`${PREFIX}.profile_details.gender`)}</span>
              <CustomTooltip
                content={t(`${PREFIX}.profile_details.why_gender`)}
              />
            </label>
            <div data-testid="gender-container">
              <StravaDropdownSelect
                key={`${INPUT_IDS.gender}-${alert?.id}`}
                name={INPUT_IDS.gender}
                options={genderOptions}
                value={genderOptions.find((o) => o.value === gender)}
                onChange={handleGenderChange}
                ariaLabel={t(`${PREFIX}.profile_details.gender`)}
                hasError={alert?.target === INPUT_IDS.gender}
                isFocused={isInvalidGender}
                styles={genderControlCustomStyles}
              />
            </div>
          </div>
        </div>
      </ModalBodyWithAlerts>

      <OnboardingModalActions>
        <ActionButton
          variant="primary"
          onClick={stepForward}
          data-cy="create-profile-next-step-btn"
          isProcessing={isProcessing}
        >
          {t('cta_actions.continue')}
        </ActionButton>
      </OnboardingModalActions>
    </>
  );
};

const CustomTooltip = ({ content }) => {
  return (
    <div
      className={styles.toolTipContainer}
      role="tooltip"
      aria-label={content}
    >
      <Tooltip
        label={<Markup noWrap={true} content={content} />}
        position="top"
        className={styles.toolTip}
      >
        <NavigationHelpNormalXsmall />
      </Tooltip>
    </div>
  );
};

CustomTooltip.propTypes = {
  content: PropTypes.string.isRequired
};

export default CreateProfile;
